// SolidWorks' "shaded with edges": a faint black line wherever the model's
// SURFACE breaks — a crease, a corner, an opening's jamb, a silhouette.
//
// Found on screen, not in the geometry. The model is built from many prisms (a
// wall's solid runs and the lintel over its door, a room's wall bands, bench
// slats), and outlining each prism draws every joint between two of them, even
// where their faces are one flat surface. So the content is rendered once more
// to a target holding its view-space NORMALS and DEPTH, and a full-screen pass
// marks a pixel where the normal turns by more than `EDGE_ANGLE_DEG` or the depth
// steps (a discontinuity of 1/z, which is linear across any plane). Coplanar
// pieces have the same normal and continuous depth, so they read as one face
// wherever the model happens to be split; a tessellated curve, whose facets turn
// by a few degrees each, shows only its silhouette.
//
// Nothing is added to the model, so no exporter (glTF, OBJ, STL, USDZ, 3D DXF)
// ever sees a line. A surface that writes no depth (the hot tub's water) is left
// out of the pass.
//
// Under a section plane the pass is clipped as the view is, and the cut is ONE
// flat face: the section's caps (the back faces seen through the cut) write the
// plane's normal, flagged in alpha, and the edge pass reads the plane's own depth
// there — the back faces' would draw every inside corner of a cut wall.

import * as THREE from 'three';

export const EDGE_ANGLE_DEG = 30;
export const EDGE_OPACITY = 0.35;
/** The 1/z discontinuity, relative to 1/z, that reads as a depth step. */
export const EDGE_DEPTH_STEP = 0.02;

const VERT = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
#include <packing>
uniform sampler2D tNormal;
uniform sampler2D tDepth;
uniform vec2 texel;
uniform float near;
uniform float far;
uniform float opacity;
uniform float cosAngle;
uniform float depthStep;
uniform float capOn;
uniform vec4 capPlane;
uniform mat4 projInv;
varying vec2 vUv;
float depthAt(vec2 uv) { return texture2D(tDepth, uv).x; }
float invZ(float d) { return d >= 1.0 ? 0.0 : -1.0 / perspectiveDepthToViewZ(d, near, far); }
vec3 normalAt(vec2 uv) { return normalize(texture2D(tNormal, uv).xyz * 2.0 - 1.0); }
// 1/z of a pixel: a section's cap (alpha 0.5) stands on the plane, wherever the
// back face it was drawn on lies.
float izAt(vec2 uv) {
  float d = depthAt(uv);
  if (d >= 1.0) return 0.0;
  float a = texture2D(tNormal, uv).a;
  if (capOn > 0.5 && a > 0.25 && a < 0.75) {
    vec4 q = projInv * vec4(uv * 2.0 - 1.0, 1.0, 1.0);
    vec3 dir = q.xyz / q.w;
    float den = dot(capPlane.xyz, dir);
    if (abs(den) > 1e-9) {
      float z = (-capPlane.w / den) * dir.z;
      if (z < 0.0) return -1.0 / z;
    }
  }
  return invZ(d);
}
void main() {
  float d = depthAt(vUv);
  if (d >= 1.0) discard;                       // background: the model's own pixel carries its silhouette
  vec2 dx = vec2(texel.x, 0.0), dy = vec2(0.0, texel.y);
  float iz = izAt(vUv);
  float l = izAt(vUv - dx), r = izAt(vUv + dx);
  float b = izAt(vUv - dy), t = izAt(vUv + dy);
  float lap = abs(l + r - 2.0 * iz) + abs(b + t - 2.0 * iz);
  bool step = lap > depthStep * iz;
  // A crease: compared with the next pixel right and up only, so it is one pixel wide.
  vec3 n = normalAt(vUv);
  bool crease = (r > 0.0 && dot(n, normalAt(vUv + dx)) < cosAngle)
    || (t > 0.0 && dot(n, normalAt(vUv + dy)) < cosAngle);
  if (!step && !crease) discard;
  gl_FragColor = vec4(0.0, 0.0, 0.0, opacity);
}
`;

/** A section's cap in the normals pass: the plane's normal (view space, toward
 *  the eye), flagged by alpha 0.5, over the back faces the cut shows. */
const CAP_VERT = /* glsl */ `
#include <clipping_planes_pars_vertex>
void main() {
  #include <begin_vertex>
  #include <project_vertex>
  #include <clipping_planes_vertex>
}
`;
const CAP_FRAG = /* glsl */ `
uniform vec3 capNormal;
#include <clipping_planes_pars_fragment>
void main() {
  #include <clipping_planes_fragment>
  gl_FragColor = vec4(capNormal * 0.5 + 0.5, 0.5);
}
`;

export class EdgePass {
  private target = new THREE.WebGLRenderTarget(1, 1, { depthTexture: new THREE.DepthTexture(1, 1) });
  private normalScene = new THREE.Scene();
  private normalMat = new THREE.MeshNormalMaterial({ side: THREE.DoubleSide });
  private quadScene = new THREE.Scene();
  private quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private mat: THREE.ShaderMaterial;
  private capScene = new THREE.Scene();
  private capMat = new THREE.ShaderMaterial({
    uniforms: { capNormal: { value: new THREE.Vector3(0, 0, 1) } },
    vertexShader: CAP_VERT,
    fragmentShader: CAP_FRAG,
    side: THREE.BackSide,
    clipping: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -4,
  });
  /** The section plane the view is clipped by (three's frame), or null. */
  private clip: THREE.Plane | null = null;

  constructor(private renderer: THREE.WebGLRenderer) {
    this.normalScene.overrideMaterial = this.normalMat;
    this.capScene.overrideMaterial = this.capMat;
    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        tNormal: { value: this.target.texture },
        tDepth: { value: this.target.depthTexture },
        texel: { value: new THREE.Vector2(1, 1) },
        near: { value: 1 },
        far: { value: 1000 },
        opacity: { value: EDGE_OPACITY },
        cosAngle: { value: Math.cos((EDGE_ANGLE_DEG * Math.PI) / 180) },
        depthStep: { value: EDGE_DEPTH_STEP },
        capOn: { value: 0 },
        capPlane: { value: new THREE.Vector4() },
        projInv: { value: new THREE.Matrix4() },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    this.quadScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.mat));
  }

  /** Match the drawing buffer, in device pixels. */
  setSize(width: number, height: number): void {
    this.target.setSize(width, height);
    (this.mat.uniforms.texel.value as THREE.Vector2).set(1 / width, 1 / height);
  }

  /** Clip the pass by the view's section plane (the same object, so a move
   *  needs no call), or by none. */
  setSection(plane: THREE.Plane | null): void {
    this.clip = plane;
    this.normalMat.clippingPlanes = plane ? [plane] : null;
    this.capMat.clippingPlanes = plane ? [plane] : null;
    this.mat.uniforms.capOn.value = plane ? 1 : 0;
  }

  /** Draw the edges of `content` over what the renderer has just drawn; `caps`,
   *  the section's caps (`Viewer3D`), read as the plane; `unseen`, the meshes
   *  left out — what writes no depth (the hot tub's water, what X-ray sees
   *  through: the viewer's records say which). */
  render(content: THREE.Object3D | null, camera: THREE.PerspectiveCamera, caps: THREE.Object3D | null, unseen: (o: THREE.Object3D) => boolean): void {
    if (!content) return;
    const r = this.renderer;
    const parent = content.parent;
    const hidden: THREE.Object3D[] = [];
    content.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || !mesh.visible) return;
      if (unseen(mesh)) { mesh.visible = false; hidden.push(mesh); }
    });
    // Borrow the content for the normals pass (re-parenting keeps its world
    // matrices: both scenes sit at the origin), then hand it back.
    this.normalScene.add(content);
    const prevTarget = r.getRenderTarget();
    const prevColor = r.getClearColor(new THREE.Color()), prevAlpha = r.getClearAlpha();
    const prevShadows = r.shadowMap.autoUpdate;
    r.shadowMap.autoUpdate = false;
    r.setRenderTarget(this.target);
    r.setClearColor(0x000000, 0);
    r.clear();
    r.render(this.normalScene, camera);
    if (this.clip && caps && caps.children.length) {
      // The plane in view space, and the cut's normal turned toward the eye.
      const view = this.clip.clone().applyMatrix4(camera.matrixWorldInverse);
      (this.capMat.uniforms.capNormal.value as THREE.Vector3).copy(view.normal).negate();
      (this.mat.uniforms.capPlane.value as THREE.Vector4).set(view.normal.x, view.normal.y, view.normal.z, view.constant);
      (this.mat.uniforms.projInv.value as THREE.Matrix4).copy(camera.projectionMatrixInverse);
      const capsParent = caps.parent;
      this.capScene.add(caps);
      const prevAutoClear = r.autoClear;
      r.autoClear = false;
      r.render(this.capScene, camera);
      r.autoClear = prevAutoClear;
      if (capsParent) capsParent.add(caps); else this.capScene.remove(caps);
    }
    r.setRenderTarget(prevTarget);
    r.setClearColor(prevColor, prevAlpha);
    r.shadowMap.autoUpdate = prevShadows;
    if (parent) parent.add(content); else this.normalScene.remove(content);
    for (const o of hidden) o.visible = true;
    this.mat.uniforms.near.value = camera.near;
    this.mat.uniforms.far.value = camera.far;
    const prevAuto = r.autoClear;
    r.autoClear = false;
    r.render(this.quadScene, this.quadCam);
    r.autoClear = prevAuto;
  }

  dispose(): void {
    this.target.dispose();
    this.target.depthTexture?.dispose();
    this.normalMat.dispose();
    this.capMat.dispose();
    this.mat.dispose();
  }
}
